// ERTSafetyEquipmentTable.tsx
import React from 'react';

export interface ERTSafetyEquipmentRow {
  srNo: number;
  typeOfEquipment: string;
  responsibleDepartment: string;
  totalQuantity: number | null;
  condition: string | null;
  lastInspectionDate: string | null;
  remarks: string | null;
}

interface ERTSafetyEquipmentTableProps {
  data?: ERTSafetyEquipmentRow[];
  onChange?: (index: number, updatedRow: ERTSafetyEquipmentRow) => void;
  isEditable?: boolean;
}

const conditionOptions = ['Good', 'Fair', 'Poor', 'N/A'];

export default function ERTSafetyEquipmentTable({
  data = [],
  onChange,
  isEditable = true,
}: ERTSafetyEquipmentTableProps) {
  const handleFieldChange = (
    idx: number,
    field: keyof ERTSafetyEquipmentRow,
    value: string | number | null
  ) => {
    if (!onChange || !isEditable) return;

    const row = data[idx];
    let processedValue: string | number | null = value;

    if (field === 'totalQuantity') {
      if (typeof value === 'string') {
        if (value === '') {
          processedValue = null;
        } else {
          const numValue = parseFloat(value);
          processedValue = isNaN(numValue) ? null : numValue;
        }
      }
    }

    // Prevent editing of read-only fields
    if (field === 'srNo' || field === 'typeOfEquipment' || field === 'responsibleDepartment') {
      return;
    }

    onChange(idx, {
      ...row,
      [field]: processedValue,
    });
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="bg-orange-600 text-white px-4 py-2 font-bold text-lg mb-0 rounded-t-lg">
        (IV) ERT SAFETY EQUIPMENT STATUS
      </div>
      <table className="min-w-full border-collapse border border-gray-300 bg-white">
        <thead>
          <tr className="bg-gray-200 border-b-2 border-gray-400">
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-16">
              Sr.No.
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[220px]">
              Type of Equipment
            </th>
            <th className="border border-gray-300 px-4 py-2 text-center font-bold text-sm text-gray-800 min-w-[160px]">
              Responsible Department
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-32">
              Total Quantity
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-32">
              Condition
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 min-w-[140px]">
              Last Inspection Date
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[200px]">
              Remarks
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr
              key={idx}
              className="bg-white hover:bg-orange-50 transition-colors"
            >
              {/* Sr.No */}
              <td className="border border-gray-300 px-3 py-2 text-center text-sm font-medium text-gray-700">
                {row.srNo}
              </td>

              {/* Type of Equipment – read-only */}
              <td className="border border-gray-300 px-4 py-2 text-sm text-gray-700">
                <span className="whitespace-pre-wrap break-words">{row.typeOfEquipment}</span>
              </td>

              {/* Responsible Department – read-only */}
              <td className="border border-gray-300 px-4 py-2 text-sm text-gray-700 text-center">
                <span className="whitespace-pre-wrap break-words">{row.responsibleDepartment}</span>
              </td>

              {/* Total Quantity */}
              <td className="border border-gray-300 px-3 py-2 text-center">
                {isEditable ? (
                  <input
                    type="number"
                    min="0"
                    value={row.totalQuantity !== null && row.totalQuantity !== undefined ? row.totalQuantity : ''}
                    onChange={(e) => handleFieldChange(idx, 'totalQuantity', e.target.value)}
                    placeholder="—"
                    className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                ) : (
                  <span className="text-sm text-gray-700 font-medium">
                    {row.totalQuantity !== null && row.totalQuantity !== undefined ? row.totalQuantity : 'N/A'}
                  </span>
                )}
              </td>

              {/* Condition */}
              <td className="border border-gray-300 px-3 py-2 text-center min-w-[120px]">
                {isEditable ? (
                  <select
                    value={row.condition || ''}
                    onChange={(e) => handleFieldChange(idx, 'condition', e.target.value || null)}
                    className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select</option>
                    {conditionOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm text-gray-700 font-medium inline-block px-2 py-1 bg-gray-100 rounded">
                    {row.condition || 'N/A'}
                  </span>
                )}
              </td>

              {/* Last Inspection Date */}
              <td className="border border-gray-300 px-3 py-2 text-center">
                {isEditable ? (
                  <input
                    type="date"
                    value={row.lastInspectionDate || ''}
                    onChange={(e) => handleFieldChange(idx, 'lastInspectionDate', e.target.value || null)}
                    className="w-full px-2 py-1 border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <span className="text-sm text-gray-700 font-medium">
                    {row.lastInspectionDate || 'N/A'}
                  </span>
                )}
              </td>

              {/* Remarks */}
              <td className="border border-gray-300 px-4 py-2">
                {isEditable ? (
                  <textarea
                    value={row.remarks || ''}
                    onChange={(e) => handleFieldChange(idx, 'remarks', e.target.value || null)}
                    rows={2}
                    placeholder="Enter remarks"
                    className="w-full px-2 py-1 border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-y text-sm"
                  />
                ) : (
                  <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {row.remarks || 'N/A'}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
